import Icon from 'components/base-ui/Icon'
import dayjs from 'dayjs'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import useResizeObserver from 'hooks/useResizeObserver'
import useLanguage from 'hooks/useLanguage'
import { displayRemainingTime } from 'helpers/dateTime'
import { LanguageEnum } from 'enums'
import { LabelSemiBoldSmall } from 'components/base-ui/FontsV2'

type Props = {
  endTime: string | null
  resizeCallback?: (element: HTMLDivElement | null) => void
}

const TopLabel: FC<Props> = ({ endTime, resizeCallback }) => {
  const topLabelRef = useRef<HTMLDivElement>(null)
  const { getPropertyByLang } = useLanguage()

  useResizeObserver({
    onResize: () => {
      resizeCallback && resizeCallback(topLabelRef.current)
    },
    element: topLabelRef.current!,
  })

  const [remainingTime, setRemainingTime] = useState({
    hours: 0,
    minutes: 0,
  })
  const [isTimeup, setIsTimeup] = useState(false)
  const { t } = useTranslation()
  const countdownTimerref = useRef<any>(null)

  const countdown = useCallback(() => {
    try {
      const now = dayjs()
      const _endTime = dayjs(endTime)
      setIsTimeup(now.isAfter(_endTime))
      const duration = dayjs.duration(_endTime.diff(now))
      setRemainingTime({
        hours: duration.hours(),
        minutes: duration.minutes() + 1,
      })
    } catch (error) {
      console.error('Error in countdown calculation:', error)
      setIsTimeup(true)
      setRemainingTime({ hours: 0, minutes: 0 })
    }
  }, [endTime])

  useEffect(() => {
    if (endTime) {
      clearInterval(countdownTimerref.current)
      countdownTimerref.current = setInterval(() => countdown(), 1000)
      countdown()
    }
    return () => {
      if (countdownTimerref.current) {
        clearInterval(countdownTimerref.current)
      }
    }
  }, [countdown, endTime])

  if (!endTime) return null

  const isMoreThan10Minutes = remainingTime.hours > 0 || remainingTime.minutes >= 10
  const labelType = isMoreThan10Minutes ? 'info' : 'error'
  const title = isTimeup ? t('buffet.label.timeUp') : t('buffet.label.remainingTime')
  const remainingTimeContent = displayRemainingTime(
    endTime,
    getPropertyByLang({ en: LanguageEnum.en, th: LanguageEnum.th })
  )

  return (
    <LabelWraper id="top-label" type={labelType}>
      <TitleWrapper>
        <Icon name="clock-4" />
        <LabelSemiBoldSmall color={isTimeup ? 'red700' : 'base'}>{title}</LabelSemiBoldSmall>
      </TitleWrapper>
      {!isTimeup && <LabelSemiBoldSmall>{remainingTimeContent}</LabelSemiBoldSmall>}
    </LabelWraper>
  )
}

export default TopLabel

const LabelWraper = styled.div<{ type: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 8px 18px;

  ${({ theme, type }) =>
    type === 'error' &&
    `
      background-color: ${theme.colors.red100}; 
      color: #eb5757;
  `}

  ${({ type }) =>
    type === 'info' &&
    `
      background-color: #E9F0F7; 
      color: #407CB4;
  `}
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
`
